/* Core types and constants for the Vectress rules engine (v3). */

export type Side = 0 | 1;
export const WHITE: Side = 0;   // starts on rows 1-3, faces row 9, moves first
export const BLACK: Side = 1;   // starts on rows 7-9, faces row 1
export const other = (s: Side): Side => (s === 0 ? 1 : 0);

export type ShapeId = 0 | 1 | 2 | 3;
export const TRIANGLE: ShapeId = 0;
export const SQUARE: ShapeId = 1;
export const RHOMBUS: ShapeId = 2;
export const OCTAGON: ShapeId = 3;

/** +1 = red (+) pole, -1 = blue (-) pole, 0 = neutral (a mixed corner). */
export type Pole = 1 | -1 | 0;

export const N = 9;
export const CELLS = N * N;
export const MAX_PIECES = 18;
export const PIECES_PER_SIDE = 9;
/** Safety cap on wave resolution. The rules say "until nothing moves"; tests assert this is never reached. */
export const MAX_WAVES = 64;

export interface Move {
  piece: number;
  dr: number;
  dc: number;
  dist: 1 | 2;
  /** true = pull this piece out of its body and move it alone (D4). Only generated for pieces that are in a body. */
  solo: boolean;
}

export type Event =
  | { kind: 'move'; piece: number; from: number; to: number; dist: 1 | 2; solo: boolean; body: number[] }
  | { kind: 'unbond'; piece: number; partners: number[] }
  | { kind: 'drag-off'; piece: number; by: number }
  | { kind: 'wave'; wave: number }
  /** a magnetic contact between actor a and neighbour b in board direction (dr,dc); pure information for the presentation layer */
  | { kind: 'contact'; wave: number; a: number; b: number; dr: number; dc: number; effect: 'push' | 'attract' }
  | { kind: 'cancel'; wave: number; body: number[] }
  | { kind: 'push'; wave: number; body: number[]; dr: number; dc: number; from: number[]; to: number[] }
  | { kind: 'pushed-off'; wave: number; piece: number }
  | { kind: 'blocked'; wave: number; body: number[]; dr: number; dc: number; reason: 'blocked' | 'contested' | 'jammed' }
  | { kind: 'bond'; wave: number; a: number; b: number }
  | { kind: 'cutoff'; wave: number };

export interface Rules {
  /** The game ends after this many plies; material decides (D3). */
  plyCap: number;
  /** You lose when this many of your pieces have left the board (D2). */
  loseAtLost: number;
  /** You lose when your Core (octagon) leaves the board (D2). */
  coreWin: boolean;
  /** A move may not recreate the board as it was before the opponent's last move (D3). */
  ko: boolean;
  /** The third occurrence of a position ends the game; material decides (D3). */
  threefold: boolean;
}

export const DEFAULT_RULES: Rules = { plyCap: 200, loseAtLost: 5, coreWin: true, ko: true, threefold: true };

export type EndReason = 'core' | 'material' | 'threefold' | 'cap' | 'passes';

export interface GameResult {
  /** null = draw */
  winner: Side | null;
  reason: EndReason;
}
